<script lang="ts">
  // Aktion ohne Rahmen (docs/gestaltung.md, Regel 5): ein Symbol, die
  // Beschriftung steht im Tooltip und für Screenreader im aria-label.
  //
  // Gilt für Aktionen an einer Zeile und seit 25.09. auch für die
  // Werkzeuge in der Titelzeile einer Tabelle ("Zurücksetzen", "Filtern";
  // Tim: "nur das Icon, dann wirkt es aufgeräumter"). Beschriftet bleibt
  // nur "Bearbeiten" -- das ist ein Modus, kein Handgriff.
  //
  // `pressed` macht daraus einen Umschalter: aria-pressed und der
  // Akzent zeigen, dass er an ist (Farbe = Zustand).
  import Icon, { type IconName } from "./Icon.svelte";
  import { KREUZ_PX } from "./kreuz";

  interface Props {
    icon: IconName;
    label: string;
    onclick: (e: MouseEvent) => void;
    disabled?: boolean;
    /** Für Auf-/Zuklapp-Knöpfe: setzt aria-expanded und dreht das Symbol
     *  um 90° -- mit `chevron-right` (docs/symbole.md: EINE Form für
     *  Auf/Zu, wie die Aufklapp-Spalte der Tabellen und der Baum). */
    expanded?: boolean;
    /** Für Umschalter: setzt aria-pressed und färbt den Knopf, solange er an ist. */
    pressed?: boolean;
    /** Scharf geschaltet: die zweite Geste einer folgenreichen Aktion
     *  (Neustart rot, Upgrade gelb). Farbe = Zustand -- das Symbol sagt
     *  „ein Klick noch, dann passiert es". Auch an der gefüllten
     *  Hauptaktion (`haupt` mit „Akzent kräftig": data-ton in app.css). */
    ton?: "danger" | "warn";
    /** Dieser Knopf löst einen offenen Punkt (die gelbe Marke daneben):
     *  das Symbol steht in der Farbe der Marke statt leise (.faellig in
     *  app.css) -- gelb für einen Handgriff, "danger" für einen Defekt.
     *  Scharf geschaltet (ton) gewinnt. */
    faellig?: boolean | "danger";
    size?: number;
    /** Die EINE Hauptaktion einer Seite (höchstens eine). Mit „Akzent
     *  kräftig" (Einstellungen › Farbe) ein gefüllter, beschrifteter
     *  Knopf, sonst ein Symbol wie jedes andere (.haupt in app.css).
     *  Gefüllt in der Farbe des Bereichs, wenn „Bereichsfarbe als Ort" an
     *  ist, sonst im Akzent. */
    haupt?: boolean;
    /** Kurze Beschriftung der Hauptaktion; Standard ist `label`. */
    text?: string;
    /** Eine zweite Beschriftung, für die der Knopf Platz hält, ohne sie zu
     *  zeigen -- die andere eines scharf schaltbaren Knopfs („Drucken" /
     *  „Bestätigen"). So bleibt er beim Umschalten gleich breit und an
     *  seinem Platz (Tim 30.10.2026: „Beim Drucken rutscht der Button nach
     *  rechts und ändert seine Größe"). */
    platz?: string;
    /** false: kein Tooltip, nur das aria-label (im Claude-Chat, Tim
     *  18.10.2026: „bitte keine Tooltips anzeigen"). */
    tooltip?: boolean;
    /** 28 statt 36 px Fläche, Symbol 16 px -- für dichte Kopfzeilen (Kopf
     *  des Claude-Chats, Tim 19.10.2026: „nimmt zu viel Höhe weg"). */
    klein?: boolean;
    /** Läuft etwas (0-100): Balken am unteren Rand in der Laufend-Farbe,
     *  der Knopf bleibt dabei deckend, auch gesperrt (Slicen, Tim
     *  25.10.2026: „kann der Button dann einen Fortschritt zeigen?"). */
    fortschritt?: number | null;
  }
  // 36 px Fläche, 18 px Symbol (Tim 26.09.: "könnten etwas größer
  // sein"). Vorher 28/14 -- neben Zeilentext in t-body wirkten die
  // Symbole kleiner als die Schrift und waren schwer zu treffen.
  let { icon, label, onclick, disabled = false, expanded, pressed, ton, faellig = false, size, haupt = false, text, platz, tooltip = true, klein = false, fortschritt = null }: Props = $props();

  // Das Kreuz ist überall gleich groß (KREUZ_PX, lib/kreuz.ts) -- egal,
  // welche Größe die Nachbar-Symbole einer Zeile haben.
  const groesse = $derived(icon === "x" ? KREUZ_PX : (size ?? (klein ? 16 : 18)));
</script>

<button
  type="button"
  class="relative inline-grid overflow-hidden {klein ? 'size-7' : 'size-9'} shrink-0 place-items-center rounded-lg transition disabled:pointer-events-none {fortschritt === null ? 'disabled:opacity-35' : ''} {haupt ? 'haupt' : ''} {ton === 'danger'
    ? 'bg-danger/15 text-danger hover:bg-danger/25'
    : ton === 'warn'
      ? 'bg-warning/15 text-warning hover:bg-warning/25'
      : pressed
        ? 'bg-accent/12 text-accent hover:bg-accent/18'
        : faellig && !disabled
          ? faellig === 'danger'
            ? 'faellig rot'
            : 'faellig'
          : 'text-fg-subtle hover:bg-fg/8 hover:text-fg'}"
  title={tooltip ? label : undefined}
  data-ton={ton}
  aria-label={label}
  aria-expanded={expanded}
  aria-pressed={pressed}
  aria-busy={fortschritt !== null ? true : undefined}
  {disabled}
  {onclick}
>
  {#if fortschritt !== null}
    <span class="knopf-fortschritt" style:width="{Math.min(100, Math.max(0, fortschritt))}%" aria-hidden="true"></span>
  {/if}
  <Icon name={icon} size={groesse} class="transition-transform {expanded ? 'rotate-90' : ''}" />
  {#if haupt && platz}
    <span class="haupt-text"><span class="haupt-stapel"><span>{text ?? label}</span><span class="haupt-platz" aria-hidden="true">{platz}</span></span></span>
  {:else if haupt}
    <span class="haupt-text">{text ?? label}</span>
  {/if}
</button>

<style>
  /* Beide Beschriftungen in derselben Zelle: der Knopf ist so breit wie die
     längere, zu sehen ist nur die jetzige (mittig). */
  .haupt-stapel {
    display: inline-grid;
  }
  .haupt-stapel > span {
    grid-area: 1 / 1;
    text-align: center;
  }
  .haupt-platz {
    visibility: hidden;
  }
  /* Fortschritt am unteren Rand: Laufend-Farbe (gestaltung.md, „läuft"). */
  .knopf-fortschritt {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 3px;
    background: var(--color-laeuft);
    transition: width 0.3s ease;
    pointer-events: none;
  }
</style>
